import _ from 'underscore';
import Backbone from "backbone";
import ti18n from "i18n!nls/ExportPopupi18n";

/**
 * BaseExporter - Common utilities and base class for all export formatters
 * 
 * Provides shared functionality for:
 * - XML escaping and safety
 * - Media file path generation
 * - Common calculations and conversions
 * - Error handling patterns
 */
const BaseExporter = Backbone.Model.extend({

    /**
     * Default export options that can be extended by specific exporters
     */
    getDefaultOptions: function() {
        return {
            includeDeletedNodes: false,          // Include soft-deleted content
            frameRate: 25,                       // Target frame rate
            includeWordTimestamps: false,        // Generate subtitle tracks
            mediaPathType: 'url',               // "url" | "blob" | "relative"
            reelNamePrefix: 'V',                // Prefix for reel names
            startTimecode: '00:00:00:00',       // Starting timecode offset
            projectTitle: 'Trebble Project',    // Default project title
            includeMedia: false,                // Whether to include media files in zip package
            edlFormat: 'cmx3600',               // 'cmx3600' or 'samplitude'
            sampleRate: 48000,                  // For Samplitude EDL
            outputChannels: 2,                  // For Samplitude EDL
            createTrackForEachFile: true
        };
    },

    /**
     * Calculate total duration of timeline data
     * @param {Array} timelineData - Array of timeline clip objects
     * @returns {Number} Total duration in seconds
     */
    getTotalDuration: function(timelineData) {
        if (!timelineData || timelineData.length === 0) {
            return 0;
        }
        
        // Find the maximum end time
        let maxEndTime = 0;
        timelineData.forEach(clip => {
            if (clip.endTimeInSeconds > maxEndTime) {
                maxEndTime = clip.endTimeInSeconds;
            }
        });
        
        return maxEndTime;
    },

    /**
     * Get the appropriate file path for media based on export options
     * @param {Object} clip - Timeline clip object
     * @param {Object} options - Export options
     * @returns {String} Media file path
     */
    getMediaFilePath: function(clip, options) {
        const fileName = this.getClipFileName(clip);
        if (options.mediaPathType === 'relative') {
            return `media/${fileName}`;
        } else if (options.mediaPathType === 'blob') {
            return clip.sourceMediaUrl;
        } else {
            // Default to URL - always prefer sourceMediaUrl over fileName
            return clip.sourceMediaUrl || fileName;
        }
    },

    getClipMediaKey: function(clip) {
        return clip.sourceMediaKey || clip.sourceMediaUrl || clip.packageFileName || clip.fileName || 'unknown_file';
    },

    getClipFileName: function(clip) {
        return clip.packageFileName || clip.fileName || 'unknown_file';
    },

    /**
     * Get media file path for a specific clip by filename
     * @param {String} fileName - File name to find
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} Media file path
     */
    getMediaFilePathForClip: function(fileName, timelineData, options) {
        // Find the first clip with this filename to get the source URL
        const clip = timelineData.find(c => this.getClipFileName(c) === fileName || c.fileName === fileName);
        if (clip) {
            return this.getMediaFilePath(clip, options);
        }
        return fileName; // Fallback to just the filename
    },

    /**
     * Convert linear volume (0.0-1.0) to decibels
     * @param {Number} linearVolume - Linear volume value
     * @returns {Number} Volume in decibels
     */
    convertLinearToDb: function(linearVolume) {
        if (linearVolume <= 0) {
            return -150; // Minimum dB value
        }
        return 20 * Math.log10(linearVolume);
    },

    /**
     * Escape special characters in XML content to prevent malformed XML
     * @param {String} unsafe - The unsafe string that may contain XML special characters
     * @returns {String} The escaped string safe for XML inclusion
     */
    escapeXml: function(unsafe) {
        if (!unsafe || typeof unsafe !== 'string') {
            return unsafe || '';
        }
        return unsafe.replace(/[<>&'"]/g, function (c) {
            switch (c) {
                case '<': return '&lt;';
                case '>': return '&gt;';
                case '&': return '&amp;';
                case "'": return '&apos;';
                case '"': return '&quot;';
                default: return c;
            }
        });
    },

    /**
     * Get node type string for debugging and validation
     * @abstract
     * @param {SequencerNode} sequencerNode - The sequencer node to identify
     * @returns {String} Node type identifier
     * @throws {Error} This is an abstract method that must be implemented by subclasses
     */
    getNodeType: function(sequencerNode) {
        // Acknowledge the parameter to avoid linting warnings
        void sequencerNode;
        throw new Error('getNodeType() is an abstract method and must be implemented by subclasses. ' +
                       'Please override this method to return appropriate node type strings like "word", "audio", "video", "pause", etc.');
    },

    /**
     * Extract word timestamp information from node info
     * @param {Object} nodeInfo - Node information object
     * @returns {Object|null} Word timestamp data or null if not applicable
     */
    extractWordTimestamps: function(nodeInfo) {
        const sequencerNode = nodeInfo.getSequencerNodeReference();
        
        // Basic timestamp extraction - can be overridden by specific exporters
        try {
            return {
                word: sequencerNode.getWordText ? sequencerNode.getWordText() : '',
                startTime: nodeInfo.getStartTimeOffset() / 1000,
                endTime: nodeInfo.getEndTimeOffset() / 1000,
                speaker: sequencerNode.getSpeakerId ? sequencerNode.getSpeakerId() : null,
                confidence: sequencerNode.getConfidenceScore ? sequencerNode.getConfidenceScore() : null
            };
        } catch (error) {
            console.warn('Error extracting word timestamps:', error);
            return null;
        }
    },

    /**
     * Generate error message with internationalization support
     * @param {String} baseMessage - Base error message key or text
     * @param {Error} error - Original error object
     * @returns {Error} Formatted error with i18n support
     */
    createExportError: function(baseMessage, error) {
        const message = baseMessage.includes('EXPORT_FAILED') 
            ? window.getI18n(ti18n, baseMessage) 
            : baseMessage;
        
        const fullMessage = error 
            ? `${message}: ${error.message}` 
            : message;
            
        return new Error(fullMessage);
    },

    /**
     * Validate timeline data before export
     * @param {Array} timelineData - Timeline data to validate
     * @throws {Error} If timeline data is invalid
     */
    validateTimelineData: function(timelineData) {
        if (!timelineData || !Array.isArray(timelineData)) {
            throw this.createExportError(window.getI18n(ti18n, 'EXPORT_FAILED'), 
                new Error('Invalid timeline data'));
        }
        
        if (timelineData.length === 0) {
            throw this.createExportError(window.getI18n(ti18n, 'NO_SELECTION_ERROR'));
        }
    },

    /**
     * Validate export options and apply defaults
     * @param {Object} options - Export options to validate and extend
     * @returns {Object} Validated and extended options
     */
    validateAndExtendOptions: function(options) {
        const validatedOptions = _.extend(this.getDefaultOptions(), options || {});
        
        // Validate frame rate
        if (validatedOptions.frameRate <= 0) {
            validatedOptions.frameRate = 25; // Default fallback
        }
        
        // Validate project title
        if (!validatedOptions.projectTitle || validatedOptions.projectTitle.trim() === '') {
            validatedOptions.projectTitle = 'Trebble Project';
        }
        
        return validatedOptions;
    },

    /**
     * Create track assignment map for timeline data
     * Groups clips by source file for multi-track organization
     * @param {Array} timelineData - Array of timeline clip objects
     * @returns {Map} Map of fileName -> trackNumber
     */
    createTrackAssignmentMap: function(timelineData, options) {
        const tracksBySource = new Map();
        let trackCounter = 1;
        
        timelineData.forEach(clip => {
            const mediaKey = this.getClipMediaKey(clip);
            if (mediaKey && !tracksBySource.has(mediaKey)) {
                tracksBySource.set(mediaKey, options && options.createTrackForEachFile === false ? 1 : trackCounter);
                trackCounter++;
            }
        });
        
        return tracksBySource;
    },

    createTrackGroups: function(timelineData, options) {
        const tracksBySource = this.createTrackAssignmentMap(timelineData, options);
        const groupsByTrack = new Map();

        timelineData.forEach(clip => {
            const mediaKey = this.getClipMediaKey(clip);
            const trackNumber = tracksBySource.get(mediaKey) || 1;
            if (!groupsByTrack.has(trackNumber)) {
                groupsByTrack.set(trackNumber, {
                    trackNumber: trackNumber,
                    trackName: options && options.createTrackForEachFile === false ? 'Track 1' : this.getClipFileName(clip),
                    clips: []
                });
            }
            groupsByTrack.get(trackNumber).clips.push(clip);
        });

        return Array.from(groupsByTrack.values()).sort((a, b) => a.trackNumber - b.trackNumber);
    },

    /**
     * Get unique source files from timeline data
     * @param {Array} timelineData - Array of timeline clip objects
     * @returns {Set} Set of unique file names
     */
    getUniqueSourceFiles: function(timelineData) {
        const uniqueSources = new Set();
        timelineData.forEach(clip => {
            const mediaKey = this.getClipMediaKey(clip);
            if (mediaKey) {
                uniqueSources.add(mediaKey);
            }
        });
        return uniqueSources;
    },

    /**
     * Filter timeline data by media type
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {String} mediaType - 'video' or 'audio'
     * @returns {Array} Filtered timeline data
     */
    filterByMediaType: function(timelineData, mediaType) {
        if (mediaType === 'video') {
            return timelineData.filter(clip => clip.hasVideo);
        } else if (mediaType === 'audio') {
            return timelineData; // All clips have audio
        }
        return timelineData;
    }

});

export default BaseExporter;
